import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Promise<Map<string, any> | null> | undefined} */
  #variantPricesPromise;

  /** @type {Map<string, any> | null | undefined} */
  #variantPrices;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice, { signal });
    closestSection.addEventListener('change', this.updatePriceFromPickerChange, { signal });

    this.#warmVariantPrices();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Starts loading product variant prices in the background. This keeps the cart flow untouched:
   * Shopify still confirms the selected variant before the add-to-cart button is re-enabled.
   */
  #warmVariantPrices() {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => this.#loadVariantPrices(), { timeout: 2000 });
    } else {
      window.setTimeout(() => this.#loadVariantPrices(), 250);
    }
  }

  /**
   * @returns {string | null}
   */
  #getProductUrl() {
    const root = this.closest('.shopify-section, dialog');
    const picker = root?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (!(picker instanceof HTMLElement)) return null;

    return picker.dataset.productUrl || null;
  }

  /**
   * @returns {Promise<Map<string, any> | null>}
   */
  async #loadVariantPrices() {
    if (this.#variantPrices !== undefined) return this.#variantPrices;
    if (this.#variantPricesPromise) return this.#variantPricesPromise;

    const productUrl = this.#getProductUrl();
    if (!productUrl) {
      this.#variantPrices = null;
      return null;
    }

    this.#variantPricesPromise = fetch(`${productUrl}.js`, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        if (!product?.variants?.length) return null;

        return new Map(product.variants.map((variant) => [String(variant.id), variant]));
      })
      .catch(() => null)
      .then((variantPrices) => {
        this.#variantPrices = variantPrices;
        return variantPrices;
      });

    return this.#variantPricesPromise;
  }

  /**
   * @param {HTMLElement} picker
   * @returns {string[]}
   */
  #getSelectedOptions(picker) {
    const selectedInputs = Array.from(picker.querySelectorAll('fieldset input:checked'));
    const selectedSelectOptions = Array.from(picker.querySelectorAll('select')).map((select) => {
      if (!(select instanceof HTMLSelectElement)) return null;
      return select.options[select.selectedIndex] || null;
    });

    return [...selectedInputs, ...selectedSelectOptions]
      .filter((option) => option instanceof HTMLInputElement || option instanceof HTMLOptionElement)
      .map((option) => option.value);
  }

  /**
   * @param {Map<string, any>} variantPrices
   * @param {HTMLElement} picker
   * @returns {any | null}
   */
  #findVariantFromPicker(variantPrices, picker) {
    const selectedOptions = this.#getSelectedOptions(picker);
    if (!selectedOptions.length) return null;

    return (
      Array.from(variantPrices.values()).find((variant) => {
        if (!Array.isArray(variant.options) || variant.options.length !== selectedOptions.length) return false;
        return variant.options.every((option, index) => option === selectedOptions[index]);
      }) || null
    );
  }

  /**
   * @param {number} value
   * @returns {string}
   */
  #formatPrice(value) {
    const currency = window.Shopify?.currency?.active || 'USD';
    const format = window.Shopify?.money_format || '${{amount}}';

    return formatMoney(value, format, currency);
  }

  /**
   * @param {any} variant
   */
  #applyFastPrice(variant) {
    const priceContainer = this.refs.priceContainer || this.querySelector('[ref="priceContainer"]');
    if (!priceContainer || typeof variant.price !== 'number') return;

    const priceElement = priceContainer.querySelector('.price');
    if (priceElement) {
      priceElement.textContent = this.#formatPrice(variant.price);
    }

    const compareAtPriceElement = priceContainer.querySelector('.compare-at-price');
    if (compareAtPriceElement) {
      const hasCompareAtPrice = typeof variant.compare_at_price === 'number' && variant.compare_at_price > variant.price;
      compareAtPriceElement.textContent = hasCompareAtPrice ? this.#formatPrice(variant.compare_at_price) : '';
      compareAtPriceElement.toggleAttribute('hidden', !hasCompareAtPrice);
    }
  }

  /**
   * Optimistically updates only the visible price while Shopify fetches the authoritative variant HTML.
   * It does not update the product form variant ID or enable cart buttons.
   * @param {Event} event
   */
  updatePriceFromPickerChange = async (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const picker = event.target.closest('variant-picker');
    if (!(picker instanceof HTMLElement)) return;
    if (picker.dataset.productId !== this.dataset.productId) return;

    const variantPrices = await this.#loadVariantPrices();
    if (!variantPrices) return;

    const variant = this.#findVariantFromPicker(variantPrices, picker);
    if (!variant) return;

    this.#applyFastPrice(variant);
  };

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
      this.#variantPrices = undefined;
      this.#variantPricesPromise = undefined;
      this.#warmVariantPrices();
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
